import { ActionIcon, Anchor, Badge, Group, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { IconDownload, IconFile, IconFileTypePdf, IconFileTypeDoc, IconFileTypeXls, IconPhoto, IconFileZip, IconX } from '@tabler/icons-react';
import { fileExtension, formatBytes, type FileRefDto } from '@mexp/shared';
import { notifyError } from '@/lib/notify';
import { openFile } from './files';

export function FileIcon({ name }: { name: string }) {
  const ext = fileExtension(name);
  const Icon =
    ext === 'pdf'
      ? IconFileTypePdf
      : ['doc', 'docx', 'odt', 'rtf'].includes(ext)
        ? IconFileTypeDoc
        : ['xls', 'xlsx', 'xlsm', 'csv', 'ods'].includes(ext)
          ? IconFileTypeXls
          : ['jpg', 'jpeg', 'png', 'gif', 'webp', 'tif', 'tiff', 'heic'].includes(ext)
            ? IconPhoto
            : ['zip', 'rar', '7z'].includes(ext)
              ? IconFileZip
              : IconFile;
  const color = ext === 'pdf' ? 'red' : ['doc', 'docx'].includes(ext) ? 'blue' : ['xls', 'xlsx', 'csv'].includes(ext) ? 'green' : 'gray';
  return (
    <ThemeIcon variant="light" color={color} size={30} radius="md">
      <Icon size={18} stroke={1.6} />
    </ThemeIcon>
  );
}

export function FileRow({ file, onRemove }: { file: FileRefDto; onRemove?: () => void }) {
  const open = (disposition?: 'inline' | 'attachment') => void openFile(file.id, disposition).catch((e) => notifyError(e, 'Could not open the file'));
  return (
    <Group gap="sm" wrap="nowrap" justify="space-between">
      <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
        <FileIcon name={file.name} />
        <Stack gap={0} style={{ minWidth: 0 }}>
          <Anchor component="button" type="button" fz="sm" onClick={() => open()} ta="left" truncate="end" maw={420}>
            {file.name}
          </Anchor>
          <Group gap={6}>
            <Text fz="xs" c="dimmed">
              {formatBytes(file.sizeBytes)}
            </Text>
            {file.status === 'legacy' && (
              <Tooltip label="Still in Google Drive; it opens the original Drive file until it is copied to MExP storage">
                <Badge size="xs" color="yellow" variant="light">
                  Drive
                </Badge>
              </Tooltip>
            )}
          </Group>
        </Stack>
      </Group>
      <Group gap={2} wrap="nowrap">
        {file.status !== 'legacy' && (
          <Tooltip label="Download">
            <ActionIcon size="sm" onClick={() => open('attachment')} aria-label={`Download ${file.name}`}>
              <IconDownload size={16} />
            </ActionIcon>
          </Tooltip>
        )}
        {onRemove && (
          <Tooltip label="Remove">
            <ActionIcon size="sm" color="red" onClick={onRemove} aria-label={`Remove ${file.name}`}>
              <IconX size={16} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
    </Group>
  );
}

export function FileList({ files, empty = 'No files', onRemove }: { files: FileRefDto[]; empty?: string; onRemove?: (id: string) => void }) {
  if (!files.length) {
    return (
      <Text fz="sm" c="dimmed">
        {empty}
      </Text>
    );
  }
  return (
    <Stack gap="xs">
      {files.map((f) => (
        <FileRow key={f.id} file={f} onRemove={onRemove ? () => onRemove(f.id) : undefined} />
      ))}
    </Stack>
  );
}
